/**
 * One scenario read beside its list: its ticket's whole thread, with whether Kim's AI turn on it was
 * recorded, or a fabricated customer's thread with who fabricated it. Why the bucketing placed a
 * ticket under its type, and the world its tools answer with, wait under the thread because they
 * settle a verdict rather than choose a run.
 */

import { useAtomValue } from 'jotai';
import { History, Loader2, Play, Ticket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ContainedScrollArea } from '@/components/ui/contained-scroll-area';
import { useTicketThread } from '@/hooks';
import { currentUserAtom } from '@/hooks/use-auth';
import { threadToTranscript } from '@/services/ticket.service';
import { MsgBubble, type TranscriptMessage } from '@/modules/simulator/conversations/conversation-parts';
import { PillButton } from '@/modules/simulator/ui/pill-button';
import { SummaryTag } from '@/modules/simulator/ui/summary';
import { humanize } from '@/modules/simulator/ui/text';
import { Disclosure } from './disclosure';
import { LevelHeader } from './level-header';
import { fabricatedBy, previewText, scenarioContact, threadText } from '../format';
import { KindBadge } from '../kind-badge';
import { ticketSource, ticketSourceHint } from '../recording/ticket-source';
import { TicketSourceBadge } from '../recording/ticket-source-badge';
import type { Scenario } from '../types';

/** The scenario's world, flattened to the rows worth reading before a run. */
const worldRows = (toolOutput: Scenario['toolOutput']): { label: string; value: string }[] =>
	Object.entries(toolOutput ?? {})
		.filter(([, value]) => (Array.isArray(value) ? value.length > 0 : typeof value === 'string' && value.trim()))
		.map(([key, value]) => ({
			label: humanize(key),
			value: Array.isArray(value) ? `${value.length} item${value.length === 1 ? '' : 's'}` : String(value)
		}));

/**
 * The turns the scenario replays, shown for a fabricated scenario and when the ticket's thread
 * cannot be loaded; they carry no timestamps, which the bubble already treats as optional.
 */
const storedTurns = (scenario: Scenario): TranscriptMessage[] =>
	scenario.query.map((turn) => ({
		role: turn.role === 'user' ? 'user' : 'agent',
		content: threadText(turn.text),
		createdAt: ''
	}));

interface ScenarioDetailProps {
	scenario: Scenario;
	onTest: (scenario: Scenario) => void;
	onViewTicket: (ticketId: string) => void;
	onHistory?: (scenario: Scenario) => void;
}

export const ScenarioDetail = ({ scenario, onTest, onViewTicket, onHistory }: ScenarioDetailProps) => {
	const currentUser = useAtomValue(currentUserAtom);
	const { name, email } = scenarioContact(scenario);
	const rows = worldRows(scenario.toolOutput);
	const { ticketId } = scenario;
	// The ticket's whole thread; the stored turns are only its opening, which the scenario replays.
	const { data: thread, isLoading: threadLoading, isError: threadError } = useTicketThread(ticketId);
	const transcript: TranscriptMessage[] = thread
		? threadToTranscript(thread).map((m) => ({ ...m, content: threadText(m.content) }))
		: storedTurns(scenario);

	return (
		<div className='flex min-h-0 flex-1 flex-col'>
			<LevelHeader
				// Who wrote in, and nothing else: the thread below is the message, so
				// repeating its opening under the name says it twice.
				title={name ?? email ?? previewText(scenario.description)}
				subtitle={name && email ? email : undefined}
				actions={
					<>
						{ticketId ? (
							<>
								<TicketSourceBadge scenario={scenario} />
								<PillButton onClick={() => onViewTicket(ticketId)}>
									<Ticket className='size-3' />#{ticketId}
								</PillButton>
							</>
						) : (
							<KindBadge kind={scenario.kind} />
						)}
						{onHistory && (
							<Button
								variant='ghost'
								size='icon'
								className='size-7 text-gray-900'
								onClick={() => onHistory(scenario)}
								aria-label='Past runs'
								title='Past runs'
							>
								<History className='size-3.5' />
							</Button>
						)}
						<PillButton variant='primary' onClick={() => onTest(scenario)}>
							<Play className='size-3' />
							Test in simulator
						</PillButton>
					</>
				}
			/>

			<ContainedScrollArea>
				<div className='flex flex-col gap-4 px-4 py-3'>
					{scenario.kind === 'fabricated' && (
						<p className='text-xs leading-4 text-gray-800'>
							{fabricatedBy(scenario.createdBy, currentUser?.id ?? null, scenario.createdAt)}: the AI wrote this
							customer&apos;s thread for the scenario type, so no human ever replied.
						</p>
					)}
					{ticketSource(scenario) === 'recorded' && (
						<p className='text-xs leading-4 text-gray-800'>{ticketSourceHint(scenario)}</p>
					)}
					{threadLoading ? (
						<p className='flex items-center justify-center gap-2 py-8 text-xs text-gray-800'>
							<Loader2 className='size-3.5 animate-spin' />
							Loading the conversation
						</p>
					) : (
						<>
							{threadError && (
								<p className='text-xs leading-4 text-gray-800'>
									The full ticket could not be loaded, so this is the thread the scenario replays.
								</p>
							)}
							<div className='flex flex-col gap-3'>
								{transcript.map((m, index) => (
									<MsgBubble key={`${scenario.id}-${index}`} m={m} />
								))}
							</div>
						</>
					)}

				</div>
			</ContainedScrollArea>

			{/* Pinned to the floor of the pane: the conversation is what is being read,
			    and this opens upward over it only when asked for. */}
			{(scenario.why || rows.length > 0) && (
				<Disclosure
					label='Scenario details'
					openLabel='Scenario details'
					className='shrink-0 border-t border-gray-500'
					bodyClassName='max-h-[40vh] overflow-y-auto'
				>
					<div className='flex flex-col gap-3'>
						{scenario.why && (
							<div className='flex flex-col gap-1'>
								<p className='text-xs leading-4 text-gray-800'>Why this past ticket sits under this scenario type</p>
								<p className='text-[13px] leading-4.5 text-text-body'>{scenario.why}</p>
							</div>
						)}
						{rows.length > 0 && (
							<div className='flex flex-col gap-1'>
								<p className='text-xs leading-4 text-gray-800'>The world its mocked tools answer with</p>
								<div className='flex flex-col'>
									{rows.map((row) => (
										<span key={row.label} className='grid grid-cols-[9rem_1fr] items-baseline gap-3 py-1'>
											<span className='text-xs leading-4 text-gray-800'>{row.label}</span>
											<span className='min-w-0 text-[13px] leading-4.5 wrap-break-word text-text-body'>
												{row.value.length < 24 ? <SummaryTag text={row.value} /> : row.value}
											</span>
										</span>
									))}
								</div>
							</div>
						)}
					</div>
				</Disclosure>
			)}
		</div>
	);
};
